import { BarChartWrapper } from '@modules/charts/components/BarChartWrapper';
import { ChartTooltipWrapper } from '@modules/charts/components/ChartTooltipWrapper';
import { ChartWidgetErrorMessage } from '@modules/charts/components/ChartWidgetErrorMessage';
import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { CircularLoader } from '@modules/common/components/CircularLoader';
import { Widgets } from '@modules/common/constants';
import { GetChartLabelOptions } from '@modules/common/types';
import { getDisplayNameFromField } from '@modules/common/utils/getDisplayNameFromField';
import { ShowInQuantifyButton } from '@modules/feed/components/ShowInQuantifyButton';
import {
  useFetchTrackedKeywordGroupByCategories,
  useSearchAnalysesWhereClause,
} from '@modules/feed/hooks';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { useSearchStore } from '@modules/feed/store/searchStore';
import { getQuantifyBarLabelWidthBuffer } from '@modules/quantify/utils';
import { LabelUpdateInProgressIcon } from '@modules/taxonomy/components/LabelUpdateInProgressIcon';
import { TaxonomyLabelStateAlertMessage } from '@modules/taxonomy/components/TaxonomyLabelStateAlertMessage';
import { getInfoMessageForLabelState } from '@modules/taxonomy/helpers';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { LabelType } from 'generated/graphql';
import { capitalize } from 'lodash';
import pluralize from 'pluralize';
import { useCallback, useEffect } from 'react';
import { TimeRange } from 'v2/core/entities';
import { FieldNames } from 'v2/core/entities/fields';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

export const TrackedKeywordsGroupedByCategoryWidget = () => {
  const theme = useTheme();

  const duration = useSearchStore(({ query }) => query.duration);
  const dispatch = useSearchStore(({ dispatch }) => dispatch);
  const whereClause = useSearchAnalysesWhereClause();

  const { data, loading, error, refetch } =
    useFetchTrackedKeywordGroupByCategories();

  const getLabelOptions: GetChartLabelOptions = useCallback(
    ({ id, size }) => {
      const dataPoint = data.find((dataPoint) => dataPoint.id === id);
      const category = dataPoint?.category;

      const isInvisibleLabel = dataPoint?.isInvisible === true;
      const isUpdateInProgressLabel = dataPoint?.isUpdateInProgress === true;

      if (!category) {
        return null;
      }

      return {
        startIcon: (
          <Stack>
            <CategoryIcon
              category={category}
              size={size}
              fill={isInvisibleLabel ? theme.palette.text.disabled : undefined}
            />
          </Stack>
        ),
        endIcon: isUpdateInProgressLabel ? (
          <Stack>
            <LabelUpdateInProgressIcon
              size={size ? size + 2 : undefined}
              labelType={LabelType.Aspect}
            />
          </Stack>
        ) : null,
        label: capitalize(category),
        isDisabled: isInvisibleLabel,
        messageNode:
          isInvisibleLabel || isUpdateInProgressLabel ? (
            <TaxonomyLabelStateAlertMessage
              variant="tooltip"
              labelType={LabelType.Aspect}
              labelState={isInvisibleLabel ? 'invisible' : 'update_in_progress'}
              typographyVariant="caption"
            />
          ) : null,
        labelTooltipTitle: isInvisibleLabel
          ? getInfoMessageForLabelState({
              labelState: 'invisible',
              labelType: LabelType.Aspect,
            })
          : null,
      };
    },
    [data, theme.palette.text.disabled],
  );

  useEffect(() => {
    dispatch({
      type: SearchActionType.SET_ARE_WIDGETS_LOADING,
      payload: {
        trackedKeywordsWidget: loading,
      },
    });

    return () => {
      dispatch({
        type: SearchActionType.SET_ARE_WIDGETS_LOADING,
        payload: {
          trackedKeywordsWidget: false,
        },
      });
    };
  }, [dispatch, loading]);

  const renderWidgetContent = () => {
    if (error) {
      return (
        <ChartWidgetErrorMessage
          onDataRefresh={() => {
            refetch?.();
          }}
        />
      );
    }

    if (loading) {
      return <CircularLoader />;
    }

    if (data.length === 0) {
      return (
        <Typography variant="subtitle1" color="warning.light">
          No tracked keywords found for this query
        </Typography>
      );
    }

    return (
      <Box width="100%">
        <BarChartWrapper
          data={data}
          minHeight={300}
          xAxis={{
            dataKey: 'value',
            label: '# Feedback Records',
            getLabelWidthBuffer: (dataPoint) =>
              getQuantifyBarLabelWidthBuffer({
                dataPoint,
                data,
              }),
          }}
          yAxis={{
            dataKey: 'label',
            label: pluralize(getDisplayNameFromField(FieldNames.Entity)),
          }}
          getLabelOptions={getLabelOptions}
          sxProps={{
            px: 2.5,
          }}
          renderTooltipContent={({
            showTooltip,
            dataIndex,
            tooltipPosition,
            setShowTooltip,
            setTooltipPosition,
          }) => {
            return (
              <ChartTooltipWrapper
                showTooltip={showTooltip}
                payload={data[dataIndex ?? 0]}
                tooltipPosition={tooltipPosition}
                setShowTooltip={setShowTooltip}
                setTooltipPosition={setTooltipPosition}
                baseWhereClause={whereClause}
                fieldName={FieldNames.Entity}
                shouldShowAddToFilter
                dateRange={duration as TimeRange}
                getLabelOptions={getLabelOptions}
                formulaID={EQLFunctionName.Count}
                onFeedbackListDrawerClose={refetch}
                measuredAsCriteria={null}
                isSingleRecordQuery={false}
                shouldHideOpenInSearchOnFeedbackDrawer
              />
            );
          }}
        />
      </Box>
    );
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        height: '100%',
        minWidth: 0,
      }}
    >
      <Stack height="100%">
        <Stack direction="row" justifyContent="space-between" p={2}>
          <Typography variant="h6">Tracked Keywords by Categories</Typography>
          {data?.length > 0 && (
            <ShowInQuantifyButton widget={Widgets.TrackedKeywordCategory} />
          )}
        </Stack>
        <Stack height="100%" alignItems="center" justifyContent="center">
          {renderWidgetContent()}
        </Stack>
      </Stack>
    </Paper>
  );
};
